// On a phone the side column (scores, draw pile, discard pile) sits at the bottom of the table, and the groups posed
// use the room above it too (owner's request, November 2026): they flow around the column like text around a picture,
// the whole width at the top of the table, then beside the column.
//
// The flow comes from floats: an empty one pushes down a second, which keeps the column's place. That second float
// stays as long as the groups, not as tall as the column: the table scrolls and the column does not, so a group
// below it would pass under it, and the last ones could never be seen in full. The rows beside it are the only ones
// that scroll towards the column, and they keep clear of it.
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const QUERY = "(max-width: 639px)"; // Tailwind's `sm`: from there on the column sits at the top, beside the table

export const COLUMN_LIFT = 40; // the column's distance from the bottom of the table (it clears the Dealer)
const GAP_ABOVE = 6;
const GAP_BESIDE = 2; // every pixel counts: on 390 px these rows then hold a group of 4 cards and one of 3
export const GROUP_MARGIN = 6; // the margin on the left of each group (TableCombinations)

export function usePhoneBoard() {
  const [phone, setPhone] = useState(() => !!window.matchMedia?.(QUERY).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(QUERY);
    const update = () => setPhone(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return phone;
}

/**
 * The room kept for the column, in the table's own coordinates: `top` is where it starts, `height` runs to the end of
 * the groups or of the table. `widest` is the room a group may take, whatever the row it lands on: a group wider than
 * the rows beside the column could never fit there, and the browser would push it further down for ever.
 */
export function roomFor({ tableHeight, listHeight, columnWidth, columnHeight }) {
  const top = Math.max(0, tableHeight - columnHeight - COLUMN_LIFT - GAP_ABOVE);
  const width = columnWidth + GAP_BESIDE;
  return { top, width, height: Math.max(tableHeight, listHeight) - top, widest: `calc(100% - ${width + GROUP_MARGIN}px)` };
}

/**
 * How wide the float keeping that room is. Each group has its margin on the left. With the column on the right, the rows
 * hang that margin off the table's edge. With the column on the left, the first group's margin falls beside the column:
 * the float gives it back, or the rows would be narrower on that side. Anything as wide as `widest` (the bar for a new
 * group) would then no longer fit beside the column and would drop below the float, making the list longer, so the
 * float too, for ever.
 */
export const floatWidth = (room, left) => (left ? room.width - GROUP_MARGIN : room.width);

/**
 * Measures the table, the groups and the column while `enabled`. The column's height only ever grows here: it is
 * taller on the player's turn to draw, and the groups must not move at every turn.
 */
export function useTableRoom({ enabled, ready, tableRef, listRef, columnRef }) {
  const [room, setRoom] = useState(null);
  const tallest = useRef(0);

  useLayoutEffect(() => {
    const table = tableRef.current;
    const list = listRef.current;
    const column = columnRef.current;
    if (!enabled || !ready || !table || !list || !column) {
      setRoom(null);
      return undefined;
    }
    const measure = () => {
      tallest.current = Math.max(tallest.current, column.offsetHeight);
      const next = roomFor({
        tableHeight: table.clientHeight,
        listHeight: list.offsetHeight,
        columnWidth: Math.ceil(column.getBoundingClientRect().width),
        columnHeight: tallest.current,
      });
      setRoom((prev) => (
        prev && prev.top === next.top && prev.width === next.width && prev.height === next.height ? prev : next
      ));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    [table, list, column].forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled, ready, tableRef, listRef, columnRef]);

  return room;
}
